import { useReducer, useEffect, useContext, useCallback } from 'react'
import {
    UniversalDataTable,
    UniversalModal,
    UniversalToolBar,
    UniversalLoadingCircle,
    UniversalExcelExport,
    Grid,
    FormSection,
} from '../../UniversalComponents'
import { IconButton, TextField, Tooltip, Box } from '@mui/material'
import {
    PersonAdd as PersonAddIcon,
    Delete as DeleteIcon,
    Archive as ArchiveIcon,
    ArrowOutward as ArrowOutwardIcon,
    Restore,
    DownloadForOffline,
    AssignmentInd,
} from '@mui/icons-material'
import {
    deactivateSupportTickets,
    getSupportTickets,
    editSupportTicket,
} from '../../../apiRoutes'
import { ToastMessage } from '../../../context/ToastPopUpContext'
import { useMinimizer } from '../../../context/MinimizeContext'
import { useSocket } from '../../../context/SocketContext'
import { Authentication, useAuth } from '../../../context/AuthenticationContext'
import {
    standardColumnMapper,
    permissionsCheck,
    handleDeletion,
    useDebounce,
} from '../../../utilities/functionTools'
import TicketForm from './TicketForm'

const today = new Date().toISOString().substring(0, 10)

const Tickets = ({ ticketType }) => {
    const currentUser = useContext(Authentication)
    const { setUser } = useAuth()
    const { username, userPreferences, userProfile } = currentUser.auth
    const { superAdmin, userProfilePermissions } = userProfile

    const defaultState = {
        open: false,
        row: {},
        formCache: {},
        data: [],
        openFileNamer: false,
        openUserRegistrationModal: false,
        ticketsToActivate: [],
        activateData: [],
        viewDeactivated: false,
        excelExportName: '',
        excelSelectedRows: [],
        loading: false,
        paginationModel: { page: 0, pageSize: 50 },
        apiSearch: '',
        rowCount: 0,
        noResults: false,
        supportTicketColumns: {
            supportTicketAdmin: {
                columns: [
                    {
                        field: 'email',
                        headerName: 'EMAIL',
                        width: 200,
                    },
                    {
                        field: 'ticketStatus',
                        headerName: 'Status',
                        width: 150,
                        renderCell: (params) => (
                            <Box
                                sx={{
                                    backgroundColor:
                                        params.value === 'New'
                                            ? '#2196f3'
                                            : params.value === 'Open'
                                            ? '#4caf50'
                                            : params.value === 'In Progress'
                                            ? '#ff9800'
                                            : '#f44336',
                                    fontWeight: 600,
                                    paddingLeft: 1,
                                }}
                            >
                                {params.value}
                            </Box>
                        ),
                    },
                    {
                        field: 'ticketNumber',
                        headerName: 'TICKET NUMBER',
                        width: 150,
                    },
                    {
                        field: 'appVersion',
                        headerName: 'APP VERSION',
                        width: 150,
                    },
                    {
                        field: 'platform',
                        headerName: 'PLATFORM',
                        width: 150,
                    },
                    {
                        field: 'firstName',
                        headerName: 'FIRST NAME',
                        width: 150,
                    },
                    {
                        field: 'lastName',
                        headerName: 'LAST NAME',
                        width: 150,
                    },
                    {
                        field: 'phoneNumber',
                        headerName: 'PHONE NUMBER',
                        width: 150,
                    },
                    {
                        field: 'manufacturerName',
                        headerName: 'MANUFACTURER',
                        width: 150,
                    },
                    {
                        field: 'updatedBy',
                        headerName: 'UPDATED BY',
                        width: 150,
                    },
                ],
                density: 'standard',
            },
        },
        density:
            userPreferences?.dataTableColumns?.supportTicketAdmin?.density ||
            'standard',
    }
    const setToastMessage = useContext(ToastMessage)
    const reducer = (state, newState) => ({ ...state, ...newState })
    const [state, setState] = useReducer(reducer, defaultState)
    const { setMinimized } = useMinimizer()
    const { apiSocket } = useSocket()

    const {
        open,
        row,
        formCache,
        openFileNamer,
        excelExportName,
        ticketsToActivate,
        viewDeactivated,
        excelSelectedRows,
        supportTicketColumns,
        density,
        activateData,
        data,
        loading,
        paginationModel,
        apiSearch,
        rowCount,
        noResults,
    } = state
    const { supportTicketAdmin } = supportTicketColumns

    const supportTicketFormPermissions = superAdmin && superAdmin === true
    const toggleUserRegistrationModal = () =>
        setState({
            openUserRegistrationModal: !state.openUserRegistrationModal,
        })

    const debouncedSearchQuery = useDebounce(apiSearch, 1000)

    const onApiSearch = useCallback((query: any) => {
        setState({
            apiSearch: query,
        })
        if (query !== null) {
            setState({ paginationModel: { ...paginationModel, page: 0 } })
        }
    }, [])

    const getData = async (active: boolean) => {
        setState({ loading: true })
        let ticketData: any
        try {
            const getTickets = await getSupportTickets(active, ticketType)
            if (getTickets?.length === 0) {
                setToastMessage(
                    'No Support Tickets found - Please Try another Search',
                    'info'
                )
                setState({
                    apiSearch: null,
                    paginationModel: { page: 0, pageSize: 50 },
                    rowCount: getTickets?.length || 0,
                    loading: false,
                    noResults: true,
                })
                return
            }
            ticketData = standardColumnMapper(defaultColumns, getTickets).map(
                (ticket: any) => ({
                    ...ticket,
                    id: ticket?._id,
                })
            )

            setState({
                data: ticketData,
                rowCount: ticketData?.length,
                loading: false,
                noResults: false,
            })
        } catch (err: any) {
            let errorMessage = 'An unknown error occurred'

            if (err.response?.data?.message) {
                errorMessage = err.response.data.message
            } else if (err.response?.data?.error) {
                errorMessage = err.response.data.error
            } else if (err.message) {
                errorMessage = err.message
            }
            setState({ data: [], rowCount: 0, tickets: [] })
            setToastMessage(err, 'error')
            console.error(err, 'error')
        }
    }

    const collectColumnData = () => {
        const supportTicketColumns =
            userPreferences?.dataTableColumns?.supportTicketAdmin?.columns

        if (Array.isArray(supportTicketColumns)) {
            const mapColumns = supportTicketColumns?.map((col) => ({
                field: col?.field,
                headerName: col?.headerName,
                width: col?.width,
                isVisible: col?.isVisible,
            }))

            const format = {
                supportTicketAdmin: {
                    columns: mapColumns,
                },
            }

            setState({ ...state, supportTicketColumns: format })
        }
    }
    const submitColumnData = async () => {
        try {
            setUser({
                key: 'tickets',
                value: supportTicketColumns,
                type: 'columnData',
            })
        } catch (err: any) {
            console.error(err, 'error')
            setToastMessage(`${err}`, 'error')
        }
    }

    const handleActivation = async () => {
        try {
            let res: any
            if (ticketsToActivate.length !== 0) {
                const ticketIds = [...ticketsToActivate.ids]
                const findTicketsToActivate = data
                    .map((ticket) => {
                        if (ticketIds.includes(ticket._id)) {
                            return ticket
                        }
                    })
                    .filter((ticket) => ticket !== undefined)

                if (!viewDeactivated) {
                    res = await deactivateSupportTickets({
                        data: findTicketsToActivate,
                        activate: false,
                    })
                } else {
                    res = await deactivateSupportTickets({
                        data: findTicketsToActivate,
                        activate: true,
                    })
                }

                if (res && res.data && res.status === 200) {
                    setToastMessage(
                        `${
                            !viewDeactivated ? 'Deactivated' : 'Activated'
                        } tickets: ${res.data.map(
                            (email: string, index) => ` ${email}`
                        )}`
                    )
                    getData(!viewDeactivated)
                }
            } else {
                setToastMessage(
                    `No tickets selected to ${
                        !viewDeactivated ? 'deactivate' : 'activate'
                    }`,
                    'error'
                )
            }
        } catch (error: any) {
            console.error('Error archiving record:', error)
            setToastMessage(error?.message, 'error')
        }
    }

    // const handleDeletionData = () => {
    //     handleDeletion({
    //         activateData,
    //         data,
    //         returnKey: 'email',
    //         setState,
    //         getData,
    //         route: deleteUsers,
    //         viewDeactivated,
    //         setToastMessage,
    //         page: 'tickets',
    //     })
    // }

    // const handleDeletionCheck = () => {
    //     setToastMessage(
    //         `Are you sure you want to delete this?`,
    //         'warning',
    //         handleDeletionData
    //     )
    // }

    const setViewDeactivated = () => {
        setState({ viewDeactivated: !viewDeactivated })
    }

    const setModalVisbility = async (tableRow: any, newData: any) => {
        if (tableRow?.email) {
            getData(true)
            setState({ row: tableRow, open: !open })
        } else {
            getData(true)
            setState({
                row: defaultState.row,
                open: !open,
            })
        }
    }

    const openFileNamerModal = () => setState({ openFileNamer: !openFileNamer })
    const closeFileNamerModal = () =>
        setState({ openFileNamer: !openFileNamer })
    const handleTextFieldChange = (event: any) =>
        setState({ excelExportName: event.target.value })

    const buttons = [
        {
            buttonName: 'Add New Ticket',
            buttonIcon: <PersonAddIcon />,
            buttonFunction: setModalVisbility,
            disabled: true,
        },

        {
            buttonName: !viewDeactivated ? 'Deactivate' : 'Activate',
            buttonIcon: <ArchiveIcon />,
            buttonFunction: handleActivation,
            disabled: permissionsCheck(supportTicketFormPermissions, 'Support'),
        },
        {
            buttonName: !viewDeactivated
                ? 'View Deactivated Tickets'
                : 'View Active Tickets',
            buttonIcon: <Restore />,
            buttonFunction: setViewDeactivated,
            disabled: permissionsCheck(supportTicketFormPermissions, 'Support'),
        },
        // {
        //     buttonName: 'Delete',
        //     buttonIcon: <DeleteIcon />,
        //     buttonFunction: handleDeletionCheck,
        //     disabled: permissionsCheck(
        //         supportTicketFormPermissions,
        //         'Super Admin'
        //     ),
        //     display: !viewDeactivated ? 'none' : null,
        // },
        {
            buttonName: 'Export',
            buttonIcon: <ArrowOutwardIcon />,
            buttonFunction: openFileNamerModal,
        },
    ]

    const defaultColumns = [
        'email',
        'ticketNumber',
        'ticketStatus',
        'appVersion',
        'platform',
        'firstName',
        'lastName',
        'phoneNumber',
        'manufacturer.manufacturerName',
    ]

    const visibleColumns = [
        'email',
        'ticketNumber',
        'ticketStatus',
        'appVersion',
        'platform',
        'firstName',
        'lastName',
        'phoneNumber',
        'manufacturerName',
        'updatedBy',
    ]

    const customColumnNames = [
        'Ticket Number',
        'Ticket Status',
        'App Version',
        'Platform',
        'Email',
        'First Name',
        'Last Name',
        'Phone Number',
        'Manufacturer',
        'Updated By',
    ]

    const handleUpdatedColumnsChange = (updatedColumns) => {
        const format = updatedColumns.map((col) => ({
            field: col.field,
            headerName: col.headerName,
            width: col.width,
            isVisible: col.isVisible,
        }))

        const setDensity = state.density

        const setData = {
            supportTicketAdmin: { columns: format, density: setDensity },
        }

        setState({
            supportTicketColumns: setData,
        })
    }

    const handleDensityChange = (newDensity) => {
        setState({
            density: newDensity,
            supportTicketColumns: {
                supportTicketAdmin: {
                    ...supportTicketAdmin,
                    density: newDensity,
                },
            },
        })
    }

    useEffect(() => {
        if (!viewDeactivated) {
            getData(true)
        } else {
            getData(false)
        }
    }, [viewDeactivated])

    useEffect(() => {
        if (noResults) {
            return
        } else {
            getData(true)
        }
    }, [paginationModel?.page, debouncedSearchQuery])

    useEffect(() => {
        getData(!viewDeactivated)
        collectColumnData()
        apiSocket?.on('support-tickets', (newData) => {
            if (newData) {
                getData(!viewDeactivated)
                setToastMessage(`Tickets have just updated.`)
            }
        })
    }, [])

    return (
        <Grid container>
            <Grid container>
                <UniversalToolBar buttons={buttons} />
            </Grid>

            <Grid item xs={12}>
                <UniversalLoadingCircle
                    data={data}
                    reloadFunction={
                        !viewDeactivated
                            ? () => getData(true)
                            : () => getData(false)
                    }
                    customTimeoutMessage={
                        !viewDeactivated
                            ? 'No active tickets available.'
                            : 'No deactivated tickets available.'
                    }
                >
                    <UniversalDataTable
                        data={data}
                        apiDensity={density}
                        apiColumns={
                            supportTicketColumns?.supportTicketAdmin?.columns
                        }
                        savePreferences={() => submitColumnData()}
                        visibleColumns={[]}
                        getRowData={(tableRow) =>
                            setModalVisbility(tableRow, undefined)
                        }
                        onRowSelectionModelChange={(rowSelection) => {
                            setState({
                                ticketsToActivate: rowSelection,
                                activateData: rowSelection,
                            })
                        }}
                        pinnedColumns={{
                            left: ['ticketStatus', 'ticketNumber'],
                            right: [''],
                        }}
                        onUpdatedColumnsChange={handleUpdatedColumnsChange}
                        onDensityChange={handleDensityChange}
                        rowCount={rowCount}
                        paginationModel={paginationModel}
                        loading={loading}
                        paginationMode={'server'}
                        pageSizeOptions={[50]}
                        setPaginationModel={(data) => {
                            setState({ paginationModel: data })
                        }}
                        onApiSearch={(text) => {
                            setState({ noResults: false })
                            onApiSearch(text)
                        }}
                    />
                </UniversalLoadingCircle>
            </Grid>

            <UniversalModal
                title={
                    row.id
                        ? `TICKET: ${row.ticketNumber}`
                        : 'Support Ticket Details'
                }
                timeStamp={`${
                    row.email
                        ? ` Modified By: ${row.updatedBy} ${
                              row.updateAt
                                  ? `| Modified Date ${row.updateAt}`
                                  : ''
                          }  `
                        : ` Created by: ${username} | Modified Date: ${today} `
                }`}
                open={open}
                onClose={(tableRow) => setModalVisbility(tableRow, undefined)}
                wrapperStyle={{
                    margin: 'auto',
                    width: '90%',
                    marginTop: 5,
                }}
                draggable={true}
                minimizable={true}
            >
                <TicketForm
                    row={row}
                    formDescription={''}
                    onClose={(tableRow: any, newData?: any) =>
                        setModalVisbility(tableRow, newData)
                    }
                />
            </UniversalModal>

            <UniversalModal
                title={'Download Excel File'}
                open={openFileNamer}
                onClose={closeFileNamerModal}
                fullScreen={false}
                wrapperStyle={{
                    margin: 'auto',
                    width: '80%',
                    marginTop: 5,
                }}
            >
                <FormSection title="To export this Excel file please specify a title ">
                    <Grid item xs={12}>
                        <Grid container spacing={2}>
                            <Grid item xs={10} sx={{ my: 2 }}>
                                <TextField
                                    required
                                    id="outlined-required"
                                    label="XLXS File Name"
                                    onChange={handleTextFieldChange}
                                    fullWidth
                                />
                            </Grid>

                            <Grid item xs={2}>
                                <Tooltip title="Download Excel File">
                                    <IconButton
                                        onClick={() => {
                                            UniversalExcelExport(
                                                visibleColumns,
                                                customColumnNames,
                                                data,
                                                excelSelectedRows,
                                                `${excelExportName}.xlsx`
                                            )
                                            closeFileNamerModal()
                                        }}
                                    >
                                        <DownloadForOffline
                                            sx={{
                                                fontSize: 70,
                                            }}
                                        />
                                    </IconButton>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                </FormSection>
            </UniversalModal>
        </Grid>
    )
}

export default Tickets
